import React, { useEffect } from "react";
import ZoneSvgPoint from "../ZoneSvgPoint";
import WayPointEdge from "./WayPointEdge";
import RoomData from "../../../../interfaces/robot/rooms/room-data";
import ZoneControllerMouseState from "../../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { getSvgCoordinatesFromRosPoint } from "../../../../interfaces/robot/rooms/point";
import ZonePointType from "../../../../enums/robot/rooms/zone-point-type";
import IdTypePoint from "../../../../interfaces/robot/rooms/id-type-point";
import {
  selectSelectedRoomGates,
  selectSelectedRoomWaypoints,
  selectWaypointsData,
  selectSelectedRoomId,
  selectUnsavedWaypoints,
  selectRoomRedZones,
  selectGates,
} from "../../../../store/room/room-selectors";
import { validateWayPoints } from "../../../../interfaces/robot/rooms/waypoints";
import { validateRoomGates } from "../../../../validation/gate-validation";

const WayPointsMap: React.FC<{ scale: number; roomData: RoomData; mouseState: ZoneControllerMouseState, notSelectable?: boolean; }> = ({
  scale,
  roomData,
  mouseState,
  notSelectable = false
}) => {
  const dispatch = useSharedDispatch();

  const { setSelectedWayPoint, upsertWaypointSet } = useSharedActions();

  const selectedRoomId = useSharedSelector((state) => selectSelectedRoomId(state));
  const waypoints = useSharedSelector((state) => selectSelectedRoomWaypoints(state));
  const gates = useSharedSelector((state) => selectSelectedRoomGates(state));
  const allGates = useSharedSelector((state) => selectGates(state));
  const redZones = useSharedSelector((state) => selectRoomRedZones(state)(roomData.id));
  const waypointsData = useSharedSelector((state) => selectWaypointsData(state));
  const unsavedWaypoints = useSharedSelector((state) => selectUnsavedWaypoints(state));

  useEffect(() => {
    if (!roomData) return;

    const pendingSet = unsavedWaypoints.find(w => w.room === roomData.id);
    if (!pendingSet) return;

    const roomGatesValidationResults = validateRoomGates(roomData.id, allGates, redZones, {nodes: pendingSet.waypointSet.nodes, edges: pendingSet.waypointSet.edges});

    const waypointValidationResult = validateWayPoints({ 
      nodes: pendingSet.waypointSet.nodes, 
      edges: pendingSet.waypointSet.edges 
    });

    const validationResults = [...roomGatesValidationResults, waypointValidationResult].filter(vr => !vr.isValid);

    if (validationResults.length > 0) return;

    dispatch(upsertWaypointSet({ 
      room: roomData.id, 
      nodes: pendingSet.waypointSet.nodes, 
      edges: pendingSet.waypointSet.edges 
    }));
  }, [roomData, unsavedWaypoints, dispatch, upsertWaypointSet, gates, redZones]);

  useEffect(() => {
    if (waypointsData.selectedEdgeId != null && selectedRoomId != null) {
      dispatch(upsertWaypointSet({ room: selectedRoomId, ...waypoints }));
      dispatch(setSelectedWayPoint(null));
    }
  }, [dispatch, waypointsData.selectedEdgeId, selectedRoomId, upsertWaypointSet, setSelectedWayPoint, waypoints]);

  const displayMovingLine = () => {
    const initialNode = waypoints.nodes.find((n) => n.id === waypointsData.selectedNodeId);

    if (initialNode == null || waypointsData.mousePosition == null) {
      return null;
    }
    const linePoints = [
      getSvgCoordinatesFromRosPoint(
        { ...initialNode.point, id: initialNode.id, type: ZonePointType.WayPoint },
        roomData
      ),
      getSvgCoordinatesFromRosPoint(
        { ...waypointsData.mousePosition, id: "mousePoint", type: ZonePointType.WayPoint } as IdTypePoint,
        roomData
      ),
    ];
    return <WayPointEdge edge={undefined} svgPoints={linePoints} mouseState={mouseState} />;
  };

  if (!waypointsData.isVisible) {
    return null;
  }

  const showHover =
    mouseState === ZoneControllerMouseState.AddWayPoint ||
    mouseState === ZoneControllerMouseState.DeleteZone ||
    mouseState === ZoneControllerMouseState.DragZone;

  return (
    <React.Fragment>
      {waypointsData.selectedNodeId !== null && waypointsData.mousePosition !== null && displayMovingLine()}

      {waypoints.edges.map((e) => {
        const nodeU = waypoints.nodes.find((n) => n.id === e.u);
        const nodeV = waypoints.nodes.find((n) => n.id === e.v);
        if (nodeU === undefined || nodeV === undefined) {
          return null;
        }
        const svgPoints = [
          getSvgCoordinatesFromRosPoint({ ...nodeU.point, id: nodeU.id, type: ZonePointType.WayPoint }, roomData),
          getSvgCoordinatesFromRosPoint({ ...nodeV.point, id: nodeV.id, type: ZonePointType.WayPoint }, roomData),
        ];
        if (svgPoints.some((p) => p === null)) {
          return null;
        }

        return (
          <WayPointEdge
            key={e.id}
            edge={e}
            svgPoints={svgPoints}
            mouseState={mouseState}
            deletable={!gates.some((g) => g.mWaypoint === e.u || g.mWaypoint === e.v) && showHover}
            notSelectable={notSelectable}
          />
        );
      })}

      {waypoints.nodes.map((n) => {
        const svgPoint = getSvgCoordinatesFromRosPoint(
          { ...n.point, type: ZonePointType.WayPoint, id: n.id },
          roomData
        );
        if (svgPoint === null) {
          return null;
        }

        return (
          <ZoneSvgPoint
            key={n.id}
            scale={scale}
            point={{
              x: svgPoint?.x,
              y: svgPoint.y,
            }}
            id={n.id}
            type={ZonePointType.WayPoint}
            enlargingOnHoverEnabled={showHover && !gates.some((g) => g.mWaypoint === n.id)}
            midPointInsertAtIndex={null}
            preventPointerEvents={!showHover}
            deletable={!gates.some((g) => g.aWaypoint === n.id || g.bWaypoint === n.id || g.mWaypoint === n.id)}
            draggable={!gates.some((g) => g.mWaypoint === n.id)}
            notSelectable={notSelectable}
          />
        );
      })}
    </React.Fragment>
  );
};

export default WayPointsMap;
